'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  features: string[];
  deliveryTime: string;
}

interface ServicesOverviewProps {
  className?: string;
}

const ServicesOverview = ({ className = '' }: ServicesOverviewProps) => {
  const [visibleServices, setVisibleServices] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const services: Service[] = [
    {
      id: 1,
      title: 'Market Entry Strategy',
      description: 'Comprehensive regulatory roadmap for entering new pharmaceutical markets with optimized timelines and cost-effective approaches.',
      icon: 'GlobeAltIcon',
      features: [
        'Regulatory landscape analysis',
        'Documentation requirement mapping',
        'Timeline optimization',
        'Cost-benefit analysis'
      ],
      deliveryTime: '4-6 weeks'
    },
    {
      id: 2,
      title: 'Dossier Preparation & Submission',
      description: 'Expert preparation of regulatory dossiers meeting CTD, eCTD, and country-specific requirements for successful product registration.',
      icon: 'DocumentTextIcon',
      features: [
        'CTD/eCTD compilation',
        'Quality documentation',
        'Clinical data assessment',
        'Submission coordination'
      ],
      deliveryTime: '8-12 weeks'
    },
    {
      id: 3,
      title: 'GMP Compliance & Audit Support',
      description: 'End-to-end support for GMP compliance including gap analysis, remediation planning, and audit preparation for regulatory inspections.',
      icon: 'ShieldCheckIcon',
      features: [
        'Gap analysis & remediation',
        'Mock audit preparation',
        'SOP development',
        'Inspector liaison support'
      ],
      deliveryTime: '6-10 weeks'
    },
    {
      id: 4,
      title: 'Regulatory Intelligence',
      description: 'Continuous monitoring and analysis of regulatory changes, providing actionable insights for proactive compliance management.',
      icon: 'LightBulbIcon',
      features: [
        'Regulatory change tracking',
        'Impact assessment',
        'Compliance alerts',
        'Strategic recommendations'
      ],
      deliveryTime: 'Ongoing'
    },
    {
      id: 5,
      title: 'Product Lifecycle Management',
      description: 'Strategic support for post-approval changes, variations, renewals, and lifecycle optimization to maintain market authorization.',
      icon: 'ArrowPathIcon',
      features: [
        'Variation management',
        'Renewal coordination',
        'Label updates',
        'Post-market surveillance'
      ],
      deliveryTime: '2-8 weeks'
    },
    {
      id: 6,
      title: 'Training & Capacity Building',
      description: 'Customized training programs for regulatory affairs teams, quality assurance personnel, and management on compliance excellence.',
      icon: 'AcademicCapIcon',
      features: [
        'Regulatory affairs training',
        'GMP workshops',
        'Documentation best practices',
        'Audit readiness programs'
      ],
      deliveryTime: '1-3 days'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          services.forEach((service, index) => {
            setTimeout(() => {
              setVisibleServices((prev) => [...prev, service.id]);
            }, index * 100);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className={`py-16 bg-background ${className}`}>
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-textPrimary mb-4">
            Comprehensive Regulatory Solutions
          </h2>
          <p className="text-base text-textSecondary max-w-3xl mx-auto">
            From market entry strategy to ongoing compliance management, our expert team delivers tailored solutions that ensure regulatory success across global markets.
          </p>
        </div>

        <div ref={sectionRef} className="overflow-x-auto pb-4">
          <div className="flex gap-6 min-w-max">
          {services.map((service) => (
            <div
              key={service.id}
              className={`bg-card border border-border rounded-lg p-6 hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-500 group transform w-80 flex-shrink-0 ${
                visibleServices.includes(service.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary group-hover:scale-125 group-hover:rotate-6 transition-all duration-500 shadow-md group-hover:shadow-lg">
                  <Icon name={service.icon as any} size={32} className="text-primary group-hover:text-primary-foreground transition-colors duration-300" variant="outline" />
                </div>
                <span className="text-xs font-semibold text-accent bg-accent/10 px-3 py-1 rounded-full group-hover:bg-accent group-hover:text-white transition-all duration-300">
                  {service.deliveryTime}
                </span>
              </div>

              <h3 className="font-heading font-semibold text-lg text-textPrimary mb-3 group-hover:text-primary transition-colors duration-300 break-words">
                {service.title}
              </h3>

              <p className="text-sm text-textSecondary mb-4 leading-relaxed group-hover:text-textPrimary transition-colors duration-300 break-words">
                {service.description}
              </p>

              <div className="space-y-2 mb-6">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-2 group/feature">
                    <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0 group-hover/feature:scale-125 transition-transform duration-300" variant="solid" />
                    <span className="text-sm text-textSecondary group-hover:text-textPrimary transition-colors duration-300 break-words">{feature}</span>
                  </div>
                ))}
              </div>

              <button className="w-full py-3 bg-muted text-textPrimary font-semibold text-sm rounded-md hover:bg-primary hover:text-primary-foreground hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-center justify-center space-x-2">
                <span>Learn More</span>
                <Icon name="ArrowRightIcon" size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;